import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { EditorContent, useEditor, BubbleMenu, FloatingMenu } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TextStyle from '@tiptap/extension-text-style';
import FontSize from '@tiptap/extension-font-size';
import Table from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableHeader from '@tiptap/extension-table-header';
import TableCell from '@tiptap/extension-table-cell';
import Highlight from '@tiptap/extension-highlight';
import Color from '@tiptap/extension-color';
import TextAlign from '@tiptap/extension-text-align';
import FontFamily from '@tiptap/extension-font-family';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import Field from '../../../extensions/FieldExtension';
import { Extension } from '@tiptap/core';
import { ClickAwayListener } from "@material-ui/core";
import Placeholder from '@tiptap/extension-placeholder'

import {
    Bar, Btn, Group, EditorBox, DropdownContent,
    ColorPicker, DropdownBtn, DropdownItem, Divider, IconBtn, RemoveHighlightBtn,
    GlobalStyle,
    OptionTitle,
    OptionItem,
    OptionItemIcon,
    OptionItemDescription,
} from './styles';
import {
    AiOutlineBold, AiOutlineItalic, AiOutlineUnderline, AiOutlineStrikethrough,
    AiOutlineAlignLeft, AiOutlineAlignCenter, AiOutlineAlignRight, AiOutlineMenu,
    AiOutlineOrderedList, AiOutlineUnorderedList, AiOutlinePicture,
    AiOutlineTable, AiOutlineLine, AiOutlineHighlight, AiOutlineFontSize,
} from 'react-icons/ai';
import {
    RiFormatClear, RiDeleteColumn, RiDeleteRow,
    RiInsertColumnLeft, RiInsertColumnRight, RiInsertRowTop, RiInsertRowBottom,
    RiMergeCellsHorizontal, RiSplitCellsHorizontal, RiDeleteBin5Line,
} from 'react-icons/ri';
import { MdOutlineInsertLink, MdCloseFullscreen, MdOpenInFull } from 'react-icons/md';
import { TbTablePlus } from 'react-icons/tb';
import { FaPlug } from 'react-icons/fa';
import { IconList, IconPickerItem } from 'react-fa-icon-picker';
import { IconBaseProps } from 'react-icons';
import { BsLightningCharge } from 'react-icons/bs';
import ImageResize from 'tiptap-extension-resize-image';

export interface ComboOption {
    id_field_option?: number;
    field_id?: number;
    value: string;
    label: string;
    order?: string;
    hide?: string;
    old_id_field_option?: number;
    color?: string;
    icon?: React.ComponentType<IconBaseProps>;
    iconDynamic?: string;
    description?: string;
    type?: string;
    group?: string;
    groupIndex?: number;
}

// Create a proper indentation extension with fixed command structure
const IndentExtension = Extension.create({
    name: 'indent',

    addGlobalAttributes() {
        return [
            {
                types: ['paragraph', 'heading'],
                attributes: {
                    indent: {
                        default: 0,
                        renderHTML: attributes => {
                            if (!attributes.indent) {
                                return {};
                            }

                            return {
                                class: `indent-${attributes.indent}`,
                            };
                        },
                        parseHTML: element => {
                            const classNames = element.getAttribute('class') || '';
                            const match = classNames.match(/indent-(\d+)/);

                            if (!match) {
                                return 0;
                            }

                            return Number(match[1]);
                        },
                    },
                },
            },
        ];
    },

});

// Ensure the Underline extension is properly configured
const UnderlineExtension = Underline.extend({
    addCommands() {
        return {
            toggleUnderline: () => ({ commands }) => commands.toggleMark('underline'),
        };
    },
});

const insertLocalImage = (editor: any) => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = e => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => {
            editor.chain().focus().insertContent({
                type: 'image',
                attrs: { src: reader.result as string },
            }).run();
        };
        reader.readAsDataURL(file);
    };
    input.click();
};

// Replace px font sizes with heading styles
const fontStyles = [
    { label: 'Normal', value: 'paragraph' },
    { label: 'Título 1', value: 'h1' },
    { label: 'Título 2', value: 'h2' },
    { label: 'Título 3', value: 'h3' },
    { label: 'Título 4', value: 'h4' },
    { label: 'Texto Pequeno', value: 'small' }, // Add the small text option
];

// Extended color palette
const fontColors = [
    '#000000', '#434343', '#666666', '#999999', '#B7B7B7', '#CCCCCC', '#D9D9D9', '#EFEFEF', '#F3F3F3', '#FFFFFF',
    '#980000', '#FF0000', '#FF9900', '#FFFF00', '#00FF00', '#00FFFF', '#4A86E8', '#0000FF', '#9900FF', '#FF00FF',
    '#E6B8AF', '#F4CCCC', '#FCE5CD', '#FFF2CC', '#D9EAD3', '#D0E0E3', '#C9DAF8', '#CFE2F3', '#D9D2E9', '#EAD1DC',
];

// Highlight colors
const highlightColors = [
    '#FFFFFF', '#FFFF00', '#ffc078', '#8ce99a', '#74c0fc', '#b197fc', '#ffa8a8',
    '#FFC0CB', '#FFD700', '#98FB98', '#ADD8E6', '#FFA07A', '#D3D3D3', '#F0E68C'
];

// Add interface to expose the insertDynamicField function
interface TipTapEditorProps {
    onEditorReady?: (functions: {
        insertField: (field: { title: string, id_field: string, name: string, type: string }) => void,
        getHTML: () => string
    }) => void;
    initialContent?: string;
    onUpdate?: (html: string) => void;
    dynamicFields?: ComboOption[];
    onAddDynamicField?: (field: {
        title: string, id_field: string, name: string, type: string
    }) => void;
    disableImage?: boolean;
}

const TipTapEditor: React.FC<TipTapEditorProps> = ({
    onEditorReady,
    initialContent,
    onUpdate,
    dynamicFields,
    onAddDynamicField,
    disableImage = false
}) => {

    const initialContentRef = useRef(initialContent);

    const [showFontSizeDropdown, setShowFontSizeDropdown] = useState(false);
    const [showFontColorDropdown, setShowFontColorDropdown] = useState(false);
    const [showHighlightDropdown, setShowHighlightDropdown] = useState(false);
    const [showAlignDropdown, setShowAlignDropdown] = useState(false);
    const [showTableDropdown, setShowTableDropdown] = useState(false);
    const [showLinkInput, setShowLinkInput] = useState(false);
    const [linkUrl, setLinkUrl] = useState('');
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

    // Add these new state variables to track bubble menu dropdowns
    const [showBubbleFontColorDropdown, setShowBubbleFontColorDropdown] = useState(false);
    const [showBubbleHighlightDropdown, setShowBubbleHighlightDropdown] = useState(false);
    const [bubblePosition, setBubblePosition] = useState({ top: 0, left: 0 });
    const [showBubbleFontSizeDropdown, setShowBubbleFontSizeDropdown] = useState(false);

    // Add fullscreen state
    const [isFullScreen, setIsFullScreen] = useState(false);

    // Add new state for dynamic fields dropdown
    const [showDynamicFieldsDropdown, setShowDynamicFieldsDropdown] = useState(false);
    const [showBubbleDynamicFieldsDropdown, setShowBubbleDynamicFieldsDropdown] = useState(false);

    // Adicione estes novos estados no componente TipTapEditor
    const [searchTerm, setSearchTerm] = useState('');
    const [focusedItemIndex, setFocusedItemIndex] = useState(-1);
    const searchInputRef = useRef<HTMLInputElement>(null);

    const fontSizeRef = useRef<HTMLDivElement>(null);
    const fontColorRef = useRef<HTMLDivElement>(null);
    const highlightRef = useRef<HTMLDivElement>(null);
    const alignRef = useRef<HTMLDivElement>(null);
    const tableRef = useRef<HTMLDivElement>(null);
    const linkRef = useRef<HTMLDivElement>(null);
    const editorBoxRef = useRef<HTMLDivElement>(null);
    const bubbleFontColorRef = useRef<HTMLDivElement>(null);
    const bubbleHighlightRef = useRef<HTMLDivElement>(null);
    const bubbleFontSizeRef = useRef<HTMLDivElement>(null);
    const dynamicFieldsRef = useRef<HTMLDivElement>(null);
    const bubbleDynamicFieldsRef = useRef<HTMLDivElement>(null);

    const editor = useEditor({
        extensions: [
            StarterKit,
            Placeholder.configure({
                placeholder: 'Digite "/" para comandos...',
            }),
            TextStyle,
            FontSize,
            FontFamily,
            Color,
            ...(!disableImage ? [ImageResize.configure({ inline: true, allowBase64: true })] : []),
            Highlight.configure({
                multicolor: true,
            }),
            Table.configure({ resizable: true }),
            TableRow, TableHeader, TableCell,
            UnderlineExtension,
            TextAlign.configure({
                types: ['heading', 'paragraph'],
            }),
            Link.configure({
                openOnClick: true,
                linkOnPaste: true,
            }),
            Field, // Add the Field extension
            IndentExtension, // Add our custom indent extension
        ].filter(Boolean),
        content: initialContentRef.current,
        onUpdate: ({ editor }) => {
            const html = editor.getHTML();
            if (onUpdate) {
                onUpdate(html);
            }
        },
        // Add better parsing for the content which helps with base64 images
        parseOptions: {
            preserveWhitespace: 'full',
        },
        // Modify the selection update handler to prevent resetting content
        onSelectionUpdate: () => {
            // Don't update content on selection changes to avoid cursor jumping
            // But still allow the isActive function to work properly by not interfering with selection state
        },
        // Add additional configuration to improve focus behavior
        autofocus: false,
        editorProps: {
            // Add attributes to prevent focus stealing
            attributes: {
                spellcheck: 'false',
                'data-gramm': 'false', // Disable Grammarly and similar tools
            },
            // Improve how the editor handles clicks
            handleClick: (view, pos, event) => {
                // Only handle clicks that are directly on the editor
                if (event.target && (event.target as HTMLElement).closest('.ProseMirror')) {
                    return false; // Let TipTap handle it normally
                }
                // Prevent handling clicks outside actual content
                return true;
            },
            // Better control of when content is parsed
            transformPastedHTML: (html) => {
                // Keep the current HTML if it's replacing the entire content
                return html;
            },
        },
    });

    function deleteSlashBeforeCursor(editor: any) {
        const { state, commands } = editor;
        const { from, empty } = state.selection;
        if (!empty || from === 0) return;

        const charBefore = state.doc.textBetween(from - 1, from);
        if (charBefore !== '/') return;

        commands.command(({ tr }: { tr: any }) => {
            tr.delete(from - 1, from);
            return true;
        });
    }

    const handleClickAway = () => {
        setShowBubbleDynamicFieldsDropdown(false);
    };

    // Keep this important function exactly as it is
    const isActive = useCallback(
        (type: string, options = {}) => editor?.isActive(type, options),
        [editor]
    );

    // Calculate position for dropdowns to avoid screen edge issues
    const calculateDropdownPosition = useCallback((buttonElement: HTMLElement) => {
        if (!buttonElement) return { top: 0, left: 0 };

        const rect = buttonElement.getBoundingClientRect();
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceRight = window.innerWidth - rect.left;

        // Default position (below the button)
        let top = rect.height;
        let left = 0;

        // If not enough space below, position above
        if (spaceBelow < 200) {
            top = -200; // Height of dropdown
        }

        // If not enough space to the right, align right edge with right edge of button
        if (spaceRight < 200) {
            left = rect.width - 200; // Width of dropdown
        }

        return { top, left };
    }, []);

    // Add this function to calculate bubble menu dropdown positions
    const calculateBubbleDropdownPosition = useCallback((buttonElement: HTMLElement) => {
        if (!buttonElement) return { top: 0, left: 0 };

        const rect = buttonElement.getBoundingClientRect();

        // Position the dropdown above the bubble menu to avoid overlap
        return {
            top: window.scrollY + rect.top - 220, // Position above with enough space for the dropdown
            left: window.scrollX + rect.left - 50 // Offset to center with the button
        };
    }, []);

    // Add this new function to get cursor position
    const getCursorPosition = useCallback(() => {
        if (!editor) return { top: 0, left: 0 };

        const { state } = editor;
        const { from } = state.selection;

        // Get the DOM position of the cursor
        const coords = editor.view.coordsAtPos(from);

        return {
            top: coords.top,
            left: coords.left
        };
    }, [editor]);

    // Add or update this function to better handle dropdown positioning
    const calculateFloatingDropdownPosition = useCallback((element?: HTMLElement) => {
        // If we have an element (from toolbar), use it for positioning
        if (element) {
            const rect = element.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            const viewportWidth = window.innerWidth;

            // Calculate available space
            const spaceBelow = viewportHeight - rect.bottom;
            const spaceAbove = rect.top;

            // Default position (below the button)
            let top = rect.bottom + window.scrollY;
            let left = rect.left + window.scrollX;

            // If not enough space below and more space above, position above
            const dropdownHeight = 300;
            if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
                top = rect.top + window.scrollY - dropdownHeight;
            }

            // Ensure dropdown stays within viewport width
            const dropdownWidth = 250;
            if (left + dropdownWidth > viewportWidth) {
                left = rect.right + window.scrollX - dropdownWidth;
            }

            return { top, left };
        }

        // If no element, use cursor position (for floating menu and slash commands)
        const cursorPos = getCursorPosition();
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;

        // Convert cursor coordinates to be relative to the page
        let top = cursorPos.top + window.scrollY + 5; // 5px offset below cursor
        let left = cursorPos.left + window.scrollX;

        // Dropdown dimensions
        const dropdownHeight = 400;
        const dropdownWidth = 280;

        // Check if dropdown would go below viewport
        if (cursorPos.top + dropdownHeight > viewportHeight) {
            top = cursorPos.top + window.scrollY - dropdownHeight - 5; // Position above cursor
        }

        // Check if dropdown would go beyond right edge of viewport
        if (cursorPos.left + dropdownWidth > viewportWidth) {
            left = cursorPos.left + window.scrollX - dropdownWidth;
        }

        // Ensure dropdown doesn't go beyond left edge
        if (left < 10) {
            left = 10;
        }

        return { top, left };
    }, [getCursorPosition]);

    // Handle link button click with positioning
    const handleLinkButtonClick = useCallback(() => {
        if (isActive('link')) {
            editor?.chain().focus().extendMarkRange('link').unsetMark('link').run();
        } else {
            // Calculate position for link dropdown
            if (linkRef.current) {
                const position = calculateDropdownPosition(linkRef.current.firstChild as HTMLElement);
                setDropdownPosition(position);
            }
            setShowLinkInput(!showLinkInput);
        }
    }, [editor, isActive, showLinkInput, calculateDropdownPosition]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (fontSizeRef.current && !fontSizeRef.current.contains(event.target as Node)) {
                setShowFontSizeDropdown(false);
            }
            if (fontColorRef.current && !fontColorRef.current.contains(event.target as Node)) {
                setShowFontColorDropdown(false);
            }
            if (highlightRef.current && !highlightRef.current.contains(event.target as Node)) {
                setShowHighlightDropdown(false);
            }
            if (alignRef.current && !alignRef.current.contains(event.target as Node)) {
                setShowAlignDropdown(false);
            }
            if (tableRef.current && !tableRef.current.contains(event.target as Node)) {
                setShowTableDropdown(false);
            }
            if (dynamicFieldsRef.current && !dynamicFieldsRef.current.contains(event.target as Node)) {
                setShowDynamicFieldsDropdown(false);
            }
            if (bubbleDynamicFieldsRef.current && !bubbleDynamicFieldsRef.current.contains(event.target as Node)) {
                setShowBubbleDynamicFieldsDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Improve the insertDynamicFieldHandler function for more reliable insertion
    const insertDynamicFieldHandler = useCallback((field: {
        title: string,
        id_field: string,
        name: string,
        type: string
    }) => {
        if (editor) {
            // Ensure editor has focus first and prevent default behaviors
            editor.commands.focus();

            // Use setTimeout to ensure focus is established before insertion
            setTimeout(() => {
                // Get the current position after focus is established
                const position = editor.state.selection.anchor;

                // Insert the field with the current position
                editor.chain()
                    .insertField({
                        title: field.title,
                        id_field: field.id_field,
                        name: field.name,
                        type: field.type,
                        position: String(position),
                    })
                    .run();

                // Close dropdowns after insertion
                setShowDynamicFieldsDropdown(false);
                setShowBubbleDynamicFieldsDropdown(false);
            }, 0);
        }
    }, [editor]);

    // Update the exposed API to use the fixed version
    useEffect(() => {
        if (editor && onEditorReady) {
            onEditorReady({
                insertField: insertDynamicFieldHandler,
                getHTML: () => editor.getHTML()
            });
        }
    }, [editor, onEditorReady, insertDynamicFieldHandler]);

    /* ------ callbacks ------ */
    const setFont = useCallback(
        (styleType: string) => {
            if (styleType === 'paragraph') {
                editor?.chain().focus().setParagraph().run();
            } else if (['h1', 'h2', 'h3', 'h4'].includes(styleType)) {
                // Fix for Type 'number' is not assignable to type 'Level'
                const level = parseInt(styleType.replace('h', '')) as 1 | 2 | 3 | 4 | 5 | 6;
                editor?.chain().focus().toggleHeading({ level }).run();
            } else if (styleType === 'small') {
                // Set small text - first ensure we're in a paragraph then apply small font size
                editor?.chain().focus().setParagraph().setFontSize('10px').run();
            }
            setShowFontSizeDropdown(false);
        },
        [editor],
    );

    const setFontColor = useCallback(
        (color: string) => {
            if (editor) {
                editor.chain().focus().setColor(color).run();
                setShowFontColorDropdown(false);
            }
        },
        [editor],
    );

    const setHighlight = useCallback(
        (color: string) => {
            editor?.chain().focus().setHighlight({ color }).run();
            setShowHighlightDropdown(false);
        },
        [editor],
    );

    const setLink = useCallback(() => {
        if (!linkUrl) return;

        // Check if URL has http/https protocol, add if missing
        const url = linkUrl.match(/^https?:\/\//) ? linkUrl : `https://${linkUrl}`;

        editor?.chain().focus().setLink({ href: url }).run();
        setLinkUrl('');
        setShowLinkInput(false);
    }, [editor, linkUrl]);

    const isActiveHeading = useCallback(
        (type: string) => {
            if (type === 'paragraph') {
                return editor?.isActive('paragraph') && !editor?.isActive('heading') &&
                    editor?.getAttributes('textStyle').fontSize !== '10px';
            }
            if (['h1', 'h2', 'h3', 'h4'].includes(type)) {
                const level = parseInt(type.replace('h', ''));
                return editor?.isActive('heading', { level });
            }
            if (type === 'small') {
                return editor?.isActive('paragraph') && editor?.getAttributes('textStyle').fontSize === '10px';
            }
            return false;
        },
        [editor],
    );

    // Group dynamic fields by their group property
    const groupedDynamicFields = useMemo(() => {
        if (!dynamicFields) return {};

        return dynamicFields.reduce((groups, field) => {
            const group = field.group || 'Outros';
            if (!groups[group]) {
                groups[group] = [];
            }
            groups[group].push(field);
            return groups;
        }, {} as Record<string, typeof dynamicFields>);
    }, [dynamicFields]);

    // Sort groups by groupIndex
    const sortedFieldGroups = useMemo(() => {
        return Object.entries(groupedDynamicFields).sort((a, b) => {
            const indexA = a[1][0]?.groupIndex || 999;
            const indexB = b[1][0]?.groupIndex || 999;
            return indexA - indexB;
        });
    }, [groupedDynamicFields]);

    // Modified toolbar with dropdown menus
    const Menu = useMemo(() => editor && (
        <Bar className={isFullScreen ? "fullscreen-toolbar" : ""}>
            {/* Font Style Dropdown */}
            <Group ref={fontSizeRef}>
                <DropdownBtn
                    onClick={() => setShowFontSizeDropdown(!showFontSizeDropdown)}
                    active={!!editor.getAttributes('textStyle').fontSize}
                    title="Estilo de Texto"
                    data-tooltip="Estilo de Texto"
                    type="button"
                >
                    <AiOutlineFontSize />
                    <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                        {isActiveHeading('h1') && 'Título 1'}
                        {isActiveHeading('h2') && 'Título 2'}
                        {isActiveHeading('h3') && 'Título 3'}
                        {isActiveHeading('h4') && 'Título 4'}
                        {isActiveHeading('paragraph') && 'Normal'}
                    </span>
                </DropdownBtn>
                {showFontSizeDropdown && (
                    <DropdownContent>
                        {fontStyles.map(style => (
                            <DropdownItem
                                key={style.value}
                                onClick={() => setFont(style.value)}
                                active={isActiveHeading(style.value)}
                                type="button"
                            >
                                {style.value === 'paragraph' ? (
                                    <span>Normal</span>
                                ) : style.value === 'small' ? (
                                    <span style={{ fontSize: '0.7em' }}>Texto Pequeno</span>
                                ) : (
                                    <span style={{ fontWeight: 'bold', fontSize: `${1.5 - parseInt(style.value.replace('h', '')) * 0.2}em` }}>
                                        {style.label}
                                    </span>
                                )}
                            </DropdownItem>
                        ))}
                    </DropdownContent>
                )}
            </Group>

            <Divider />

            {/* Text Formatting */}
            <Group>
                <IconBtn
                    active={isActive('bold')}
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        editor.chain().focus().toggleBold().run();
                    }}
                    title="Negrito"
                    data-tooltip="Negrito"
                >
                    <AiOutlineBold />
                </IconBtn>
                <IconBtn
                    active={isActive('italic')}
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        editor.chain().focus().toggleItalic().run();
                    }}
                    title="Itálico"
                    data-tooltip="Itálico"
                >
                    <AiOutlineItalic />
                </IconBtn>
                <IconBtn
                    active={isActive('underline')}
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        editor.chain().focus().toggleUnderline().run();
                    }}
                    title="Sublinhado"
                    data-tooltip="Sublinhado"
                >
                    <AiOutlineUnderline />
                </IconBtn>
                <IconBtn
                    active={isActive('strike')}
                    type="button"
                    onClick={(e) => {
                        e.preventDefault();
                        editor.chain().focus().toggleStrike().run();
                    }}
                    title="Tachado"
                    data-tooltip="Tachado"
                >
                    <AiOutlineStrikethrough />
                </IconBtn>
            </Group>

            <Divider />

            {/* Font Color */}
            <Group ref={fontColorRef}>
                <DropdownBtn
                    onClick={() => setShowFontColorDropdown(!showFontColorDropdown)}
                    title="Cor do Texto"
                    data-tooltip="Cor do Texto"
                    type="button"
                >
                    <span style={{
                        color: editor.getAttributes('textStyle').color || '#000',
                        fontSize: '16px'
                    }}>A</span>
                </DropdownBtn>
                {showFontColorDropdown && (
                    <DropdownContent className="color-dropdown">
                        <ColorPicker>
                            {fontColors.map(color => (
                                <div
                                    key={color}
                                    style={{ backgroundColor: color }}
                                    onClick={() => setFontColor(color)}
                                    className={editor.getAttributes('textStyle').color === color ? 'active' : ''}
                                />
                            ))}
                        </ColorPicker>
                    </DropdownContent>
                )}
            </Group>

            {/* Highlight Color */}
            <Group ref={highlightRef}>
                <DropdownBtn
                    onClick={() => setShowHighlightDropdown(!showHighlightDropdown)}
                    title="Destaque"
                    data-tooltip="Destaque"
                    type="button"
                >
                    <AiOutlineHighlight style={{
                        color: isActive('highlight') ?
                            editor.getAttributes('highlight').color || '#FFFF00' : 'currentColor'
                    }} />
                </DropdownBtn>
                {showHighlightDropdown && (
                    <DropdownContent className="highlight-dropdown">
                        <ColorPicker>
                            {highlightColors.map(color => (
                                <div
                                    key={color}
                                    style={{ backgroundColor: color }}
                                    onClick={() => setHighlight(color)}
                                    className={isActive('highlight', { color }) ? 'active' : ''}
                                />
                            ))}
                        </ColorPicker>
                        <RemoveHighlightBtn
                            onClick={() => {
                                editor.chain().focus().unsetHighlight().run();
                                setShowHighlightDropdown(false);
                            }}
                            type="button"
                        >
                            Remover destaque
                        </RemoveHighlightBtn>
                    </DropdownContent>
                )}
            </Group>

            <Divider />

            {/* Text Alignment */}
            <Group ref={alignRef}>
                <DropdownBtn
                    onClick={() => setShowAlignDropdown(!showAlignDropdown)}
                    title="Alinhamento"
                    data-tooltip="Alinhamento"
                    type="button"
                >
                    {isActive('textAlign', { textAlign: 'left' }) && <AiOutlineAlignLeft />}
                    {isActive('textAlign', { textAlign: 'center' }) && <AiOutlineAlignCenter />}
                    {isActive('textAlign', { textAlign: 'right' }) && <AiOutlineAlignRight />}
                    {isActive('textAlign', { textAlign: 'justify' }) && <AiOutlineMenu />}
                    {!isActive('textAlign', { textAlign: 'left' }) && !isActive('textAlign', { textAlign: 'center' }) &&
                        !isActive('textAlign', { textAlign: 'right' }) && !isActive('textAlign', { textAlign: 'justify' }) && <AiOutlineAlignLeft />}
                </DropdownBtn>
                {showAlignDropdown && (
                    <DropdownContent>
                        <DropdownItem
                            onClick={() => editor.chain().focus().setTextAlign('left').run()}
                            active={isActive('textAlign', { textAlign: 'left' })}
                            type="button"
                        >
                            <AiOutlineAlignLeft style={{ marginRight: '8px' }} /> Esquerda
                        </DropdownItem>
                        <DropdownItem
                            onClick={() => editor.chain().focus().setTextAlign('center').run()}
                            active={isActive('textAlign', { textAlign: 'center' })}
                            type="button"
                        >
                            <AiOutlineAlignCenter style={{ marginRight: '8px' }} /> Centro
                        </DropdownItem>
                        <DropdownItem
                            onClick={() => editor.chain().focus().setTextAlign('right').run()}
                            active={isActive('textAlign', { textAlign: 'right' })}
                            type="button"
                        >
                            <AiOutlineAlignRight style={{ marginRight: '8px' }} /> Direita
                        </DropdownItem>
                        <DropdownItem
                            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
                            active={isActive('textAlign', { textAlign: 'justify' })}
                            type="button"
                        >
                            <AiOutlineMenu style={{ marginRight: '8px' }} /> Justificado
                        </DropdownItem>
                    </DropdownContent>
                )}
            </Group>

            <Divider />

            {/* List Control Group - for managing bulleted and numbered lists */}
            <Group>
                <IconBtn
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    title="Lista com Marcadores"
                    data-tooltip="Lista com Marcadores"
                    type="button"
                >
                    <AiOutlineUnorderedList />
                </IconBtn>
                <IconBtn
                    onClick={() => editor.chain().focus().toggleOrderedList().run()}
                    title="Lista Numerada"
                    data-tooltip="Lista Numerada"
                    type="button"
                >
                    <AiOutlineOrderedList />
                </IconBtn>
            </Group>

            <Divider />

            {/* Hyperlink Group - for adding and managing hyperlinks */}
            <Group ref={linkRef}>
                <IconBtn
                    onClick={handleLinkButtonClick}
                    title="Link"
                    data-tooltip="Inserir Link"
                    type="button"
                >
                    <MdOutlineInsertLink size={18} />
                </IconBtn>
                {showLinkInput && (
                    <DropdownContent
                        className="link-dropdown"
                        style={{
                            padding: '16px',
                            minWidth: '280px',
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
                        }}
                    >
                        <div style={{
                            marginBottom: '12px',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#333',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '8px'
                        }}>
                            Inserir Link
                        </div>
                        <input
                            type="text"
                            placeholder="https://exemplo.com"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                borderRadius: '6px',
                                border: '1px solid #DDDDDD',
                                fontSize: '14px',
                                marginBottom: '16px',
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4A86E8';
                                e.target.style.boxShadow = '0 0 0 2px rgba(74, 134, 232, 0.2)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#DDDDDD';
                                e.target.style.boxShadow = 'none';
                            }}
                            autoFocus
                        />
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px'
                        }}>
                            <Btn
                                onClick={() => setShowLinkInput(false)}
                                style={{
                                    padding: '8px 16px',
                                    background: '#f5f5f5',
                                    color: '#333',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500
                                }}
                                type="button"
                            >
                                Cancelar
                            </Btn>
                            <Btn
                                onClick={setLink}
                                style={{
                                    padding: '8px 16px',
                                    background: '#4A86E8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                                }}
                                type="button"
                            >
                                Aplicar
                            </Btn>
                        </div>
                    </DropdownContent>
                )}
            </Group>

            {!disableImage && (
                <Group>
                    <IconBtn
                        onClick={() => insertLocalImage(editor)}
                        title="Inserir Imagem"
                        data-tooltip="Inserir Imagem"
                        type="button"
                    >
                        <AiOutlinePicture />
                    </IconBtn>
                </Group>
            )}

            {/* Tables */}
            <Group ref={tableRef}>
                <DropdownBtn
                    onClick={() => setShowTableDropdown(!showTableDropdown)}
                    active={isActive('table')}
                    title="Tabela"
                    data-tooltip="Tabela"
                    type="button"
                >
                    <AiOutlineTable />
                </DropdownBtn>
                {showTableDropdown && (
                    <DropdownContent
                        className="airbnb-dropdown"
                        style={{
                            width: '200px',
                            padding: '5px',
                            borderRadius: '12px',
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.1)',
                            border: '1px solid #eaeaea',
                            overflow: 'hidden',
                            animation: 'fadeIn 0.2s ease-out',
                        }}
                    >
                        <DropdownItem
                            onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
                            className="airbnb-dropdown-item"
                            style={{
                                padding: '8px 16px',
                                display: 'flex',
                                alignItems: 'center',
                                transition: 'background 0.15s ease',
                                borderRadius: '0',
                            }}
                            type="button"
                        >
                            <TbTablePlus style={{ marginRight: '12px', fontSize: '20px', color: '#484848' }} />
                            <span style={{ fontWeight: '500', color: '#222' }}>Inserir Tabela</span>
                        </DropdownItem>

                        {isActive('table') && (
                            <>
                                <div style={{
                                    fontSize: '11px',
                                    color: '#717171',
                                    fontWeight: 600,
                                    padding: '4px 16px 8px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.7px'
                                }}>
                                    Colunas
                                </div>
                                <div className="airbnb-menu-group">
                                    <DropdownItem
                                        onClick={() => editor.chain().focus().addColumnBefore().run()}
                                        className="airbnb-dropdown-item"
                                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}
                                        type="button"
                                    >
                                        <RiInsertColumnLeft style={{ marginRight: '10px', color: '#484848' }} />
                                        <span style={{ color: '#222' }}>Adicionar Antes</span>
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() => editor.chain().focus().addColumnAfter().run()}
                                        className="airbnb-dropdown-item"
                                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}
                                        type="button"
                                    >
                                        <RiInsertColumnRight style={{ marginRight: '10px', color: '#484848' }} />
                                        <span style={{ color: '#222' }}>Adicionar Depois</span>
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() => editor.chain().focus().deleteColumn().run()}
                                        className="airbnb-dropdown-item airbnb-danger"
                                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}
                                        type="button"
                                    >
                                        <RiDeleteColumn style={{ marginRight: '10px' }} />
                                        <span>Remover Coluna</span>
                                    </DropdownItem>
                                </div>

                                <div style={{
                                    fontSize: '11px',
                                    color: '#717171',
                                    fontWeight: 600,
                                    padding: '8px 16px 8px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.7px'
                                }}>
                                    Linhas
                                </div>
                                <div className="airbnb-menu-group">
                                    <DropdownItem
                                        onClick={() => editor.chain().focus().addRowBefore().run()}
                                        className="airbnb-dropdown-item"
                                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}
                                        type="button"
                                    >
                                        <RiInsertRowTop style={{ marginRight: '10px', color: '#484848' }} />
                                        <span style={{ color: '#222' }}>Adicionar Antes</span>
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() => editor.chain().focus().addRowAfter().run()}
                                        className="airbnb-dropdown-item"
                                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}
                                        type="button"
                                    >
                                        <RiInsertRowBottom style={{ marginRight: '10px', color: '#484848' }} />
                                        <span style={{ color: '#222' }}>Adicionar Depois</span>
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() => editor.chain().focus().deleteRow().run()}
                                        className="airbnb-dropdown-item airbnb-danger"
                                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}
                                        type="button"
                                    >
                                        <RiDeleteRow style={{ marginRight: '10px' }} />
                                        <span>Remover Linha</span>
                                    </DropdownItem>
                                </div>

                                <div style={{
                                    fontSize: '11px',
                                    color: '#717171',
                                    fontWeight: 600,
                                    padding: '8px 16px 8px',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.7px'
                                }}>
                                    Células
                                </div>
                                <div className="airbnb-menu-group">
                                    <DropdownItem
                                        onClick={() => editor.chain().focus().mergeCells().run()}
                                        className="airbnb-dropdown-item"
                                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}
                                        type="button"
                                    >
                                        <RiMergeCellsHorizontal style={{ marginRight: '10px', color: '#484848' }} />
                                        <span style={{ color: '#222' }}>Mesclar Células</span>
                                    </DropdownItem>
                                    <DropdownItem
                                        onClick={() => editor.chain().focus().splitCell().run()}
                                        className="airbnb-dropdown-item"
                                        style={{ padding: '8px 16px', display: 'flex', alignItems: 'center' }}
                                        type="button"
                                    >
                                        <RiSplitCellsHorizontal style={{ marginRight: '10px', color: '#484848' }} />
                                        <span style={{ color: '#222' }}>Dividir Célula</span>
                                    </DropdownItem>
                                </div>

                                <DropdownItem
                                    onClick={() => editor.chain().focus().deleteTable().run()}
                                    className="airbnb-dropdown-item airbnb-danger"
                                    style={{
                                        padding: '10px 16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: 'rgba(255, 56, 92, 0.03)',
                                        marginTop: '4px',
                                        fontWeight: '500'
                                    }}
                                    type="button"
                                >
                                    <RiDeleteBin5Line style={{ marginRight: '10px' }} />
                                    <span>Remover Tabela</span>
                                </DropdownItem>
                            </>
                        )}
                    </DropdownContent>
                )}
            </Group>

            {/* Horizontal Divider */}
            <Group>
                <IconBtn
                    onClick={() => editor.chain().focus().setHorizontalRule().run()}
                    title="Divisor"
                    data-tooltip="Divisor"
                    data-tooltip-bottom="Divisor"
                    type="button"
                >
                    <AiOutlineLine />
                </IconBtn>
            </Group>

            {/* Add Dynamic Fields button in main toolbar */}
            {dynamicFields && dynamicFields.length > 0 && (
                <>
                    <Group ref={dynamicFieldsRef}>
                        <IconBtn
                            style={{ width: '150px' }}
                            onClick={(e) => {
                                e.preventDefault();                                
                                const position = calculateFloatingDropdownPosition();
                                setBubblePosition(position);
                                setShowBubbleDynamicFieldsDropdown(!showBubbleDynamicFieldsDropdown);
                                deleteSlashBeforeCursor(editor);
                            }}
                            title="Campos dinâmicos"
                            data-tooltip="Campos dinâmicos"
                            active={showDynamicFieldsDropdown}
                            type="button"
                        >
                            <BsLightningCharge color='red' />
                            <span style={{ marginLeft: '8px', fontSize: '13px' }}>
                                Campos dinâmicos
                            </span>
                        </IconBtn>
                    </Group>
                </>
            )}

            {/* Full Screen Toggle Button */}
            {!isFullScreen && (
                <>
                    <Divider />
                    <Group>
                        <IconBtn
                            style={{ width: '100px' }}
                            onClick={() => setIsFullScreen(!isFullScreen)}
                            title="Tela Cheia"
                            data-tooltip="Tela Cheia"
                            className="tooltip-top"
                            type="button"
                        >
                            <MdOpenInFull />
                            <span style={{ marginLeft: '8px', fontSize: '13px' }}>
                                Tela cheia
                            </span>
                        </IconBtn>
                    </Group>
                </>
            )}

            {/* Removemos o botão destacado e adicionamos um espaçador quando em tela cheia */}
            {isFullScreen && <div style={{ flex: 1 }} />}

            {/* Botão de sair integrado à barra em vez de elemento separado */}
            {isFullScreen && (
                <IconBtn
                    style={{ width: '100px' }}
                    onClick={() => setIsFullScreen(false)}
                    title="Sair da Tela Cheia"
                    data-tooltip-bottom="Fechar"
                    className="tooltip-bottom"
                    active={false}
                    type="button"
                >
                    <MdCloseFullscreen />
                    <span style={{ marginLeft: '8px', fontSize: '14px' }}>
                        Fechar
                    </span>
                </IconBtn>
            )}
        </Bar>
    ), [
        editor, showFontSizeDropdown, showFontColorDropdown, showHighlightDropdown, showAlignDropdown,
        showTableDropdown, showLinkInput, linkUrl, isActive, setFont, setFontColor,
        setHighlight, setLink, handleLinkButtonClick, dropdownPosition, isActiveHeading, isFullScreen,
        showDynamicFieldsDropdown, dynamicFields, calculateFloatingDropdownPosition, showBubbleDynamicFieldsDropdown,
        disableImage
    ]);

    // Manage body overflow when in fullscreen mode
    useEffect(() => {
        if (isFullScreen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isFullScreen]);

    // Adicionar efeito para suavizar a transição de entrada e saída do modo fullscreen
    useEffect(() => {
        if (isFullScreen) {
            // Aplicar foco após entrar no modo tela cheia com um pequeno delay
            // para garantir que a animação termine
            setTimeout(() => {
                if (editor && !editor.isFocused) {
                    //editor.commands.focus();
                }
            }, 300);
        }
    }, [isFullScreen, editor]);

    if (!editor) return null;

    // Render with fullscreen container if in fullscreen mode
    return (
        <div className={isFullScreen ? "tiptap-editor-fullscreen" : ""}>
            <GlobalStyle />
            {Menu}
            <EditorBox
                ref={editorBoxRef}
                className={isFullScreen ? "editor-fullscreen" : ""}
                onClick={() => {
                    if (editor && !editor.isFocused) {
                        editor.commands.focus();
                    }
                }}
            >
                <EditorContent editor={editor} />
            </EditorBox>

            {/* Dynamic Fields menu for main toolbar - add as floating element */}
            {showDynamicFieldsDropdown && dynamicFields && (
                <DropdownContent
                    className="dynamic-fields-dropdown portal-dropdown"
                    style={{
                        position: 'fixed', // Changed from absolute to fixed
                        top: `${bubblePosition.top}px`,
                        left: `${bubblePosition.left}px`,
                        zIndex: 1000,
                        width: '280px',
                        maxHeight: '400px',
                        overflowY: 'auto',
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        padding: '8px 0',
                        animation: 'fadeIn 0.2s cubic-bezier(0.2, 0, 0, 1)'
                    }}
                >

                    {sortedFieldGroups.map(([group, fields], groupIndex) => (
                        <div key={`group-${groupIndex}`}>
                            <div style={{
                                fontSize: '11px',
                                color: '#717171',
                                fontWeight: 600,
                                padding: '8px 16px 4px',
                                textTransform: 'uppercase',
                                letterSpacing: '0.7px'
                            }}>
                                {group}
                            </div>
                            {fields.map((field, fieldIndex) => (
                                <DropdownItem
                                    key={`field-${fieldIndex}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        if (onAddDynamicField !== undefined) {
                                            onAddDynamicField({
                                                title: field.label,
                                                id_field: field.value,
                                                name: field.value,
                                                type: field.type || "TEXT_SHORT_FIELD"
                                            });
                                        }
                                        setShowDynamicFieldsDropdown(false);
                                    }}
                                    className="field-dropdown-item"
                                    style={{
                                        padding: '8px 16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: field.color || '#222',
                                        transition: 'all 0.15s ease',
                                        borderRadius: '6px',
                                        margin: '0 6px'
                                    }}
                                    type="button"
                                >
                                    <FaPlug style={{
                                        marginRight: '10px',
                                        color: field.color || '#484848',
                                        fontSize: '14px'
                                    }} />
                                    <div>
                                        <span style={{ fontWeight: '500' }}>{field.label}</span>
                                        {field.description && (
                                            <div style={{
                                                fontSize: '12px',
                                                color: '#717171',
                                                marginTop: '2px'
                                            }}>
                                                {field.description}
                                            </div>
                                        )}
                                    </div>
                                </DropdownItem>
                            ))}
                            {groupIndex < sortedFieldGroups.length - 1 && (
                                <div style={{
                                    height: '1px',
                                    background: '#eee',
                                    margin: '8px 16px'
                                }} />
                            )}
                        </div>
                    ))}
                </DropdownContent>
            )}

            {/* bubble contextual menu - improved layout and positioning */}
            <BubbleMenu
                editor={editor}
                tippyOptions={{
                    placement: 'top',
                    maxWidth: 'none',
                    zIndex: 200
                }}>
                <Bar style={{
                    marginBottom: 0,
                    padding: '4px 6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    flexWrap: 'nowrap',
                    maxWidth: '100vw',
                    gap: '2px'
                }}>
                    {/* Add Font Style/Heading Dropdown to Bubble Menu */}
                    <Group ref={bubbleFontSizeRef}>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={
                                isActive('heading', { level: 1 }) ||
                                isActive('heading', { level: 2 }) ||
                                isActive('heading', { level: 3 }) ||
                                isActive('heading', { level: 4 })
                            }
                            onClick={() => {
                                if (bubbleFontSizeRef.current) {
                                    const position = calculateBubbleDropdownPosition(
                                        bubbleFontSizeRef.current.firstChild as HTMLElement
                                    );
                                    setBubblePosition(position);
                                    setShowBubbleFontSizeDropdown(!showBubbleFontSizeDropdown);

                                    // Close other dropdowns
                                    setShowBubbleFontColorDropdown(false);
                                    setShowBubbleHighlightDropdown(false);
                                    setShowBubbleDynamicFieldsDropdown(false);
                                }
                            }}
                            data-tooltip="Estilo de Texto"
                            type="button"
                        >
                            <span style={{ fontWeight: 'bold', fontSize: '12px' }}>
                                {isActive('heading', { level: 1 }) && 'H1'}
                                {isActive('heading', { level: 2 }) && 'H2'}
                                {isActive('heading', { level: 3 }) && 'H3'}
                                {isActive('heading', { level: 4 }) && 'H4'}
                                {!isActive('heading') && 'T'}
                            </span>
                        </IconBtn>
                    </Group>

                    <Divider style={{ height: '20px', margin: '0 2px' }} />

                    {/* Text Formatting */}
                    <Group>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('bold')}
                            onClick={() => editor.chain().focus().toggleBold().run()}
                            data-tooltip="Negrito"
                            type="button"
                        >
                            <AiOutlineBold size={13} />
                        </IconBtn>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('italic')}
                            onClick={() => editor.chain().focus().toggleItalic().run()}
                            data-tooltip="Itálico"
                            type="button"
                        >
                            <AiOutlineItalic size={13} />
                        </IconBtn>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('underline')}
                            onClick={() => editor.chain().focus().toggleUnderline().run()}
                            data-tooltip="Sublinhado"
                            type="button"
                        >
                            <AiOutlineUnderline size={13} />
                        </IconBtn>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('strike')}
                            onClick={() => editor.chain().focus().toggleStrike().run()}
                            data-tooltip="Tachado"
                            type="button"
                        >
                            <AiOutlineStrikethrough size={13} />
                        </IconBtn>
                    </Group>

                    <Divider style={{ height: '20px', margin: '0 2px' }} />

                    {/* Font Color - modified to handle proper positioning */}
                    <Group ref={bubbleFontColorRef}>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={!!editor.getAttributes('textStyle').color}
                            onClick={() => {
                                if (bubbleFontColorRef.current) {
                                    const position = calculateBubbleDropdownPosition(
                                        bubbleFontColorRef.current.firstChild as HTMLElement
                                    );
                                    setBubblePosition(position);
                                    setShowBubbleFontColorDropdown(!showBubbleFontColorDropdown);

                                    // Close other dropdowns
                                    setShowBubbleHighlightDropdown(false);
                                    setShowBubbleFontSizeDropdown(false);
                                    setShowBubbleDynamicFieldsDropdown(false);
                                }
                            }}
                            data-tooltip="Cor do Texto"
                            type="button"
                        >
                            <span style={{
                                color: editor.getAttributes('textStyle').color || '#000',
                                fontSize: '12px'
                            }}>A</span>
                        </IconBtn>
                    </Group>

                    {/* Highlight Color - modified to handle proper positioning */}
                    <Group ref={bubbleHighlightRef}>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('highlight')}
                            onClick={() => {
                                if (bubbleHighlightRef.current) {
                                    const position = calculateBubbleDropdownPosition(
                                        bubbleHighlightRef.current.firstChild as HTMLElement
                                    );
                                    setBubblePosition(position);
                                    setShowBubbleHighlightDropdown(!showBubbleHighlightDropdown);

                                    // Close other dropdowns
                                    setShowBubbleFontColorDropdown(false);
                                    setShowBubbleFontSizeDropdown(false);
                                    setShowBubbleDynamicFieldsDropdown(false);
                                }
                            }}
                            data-tooltip="Destaque"
                            type="button"
                        >
                            <AiOutlineHighlight size={13} />
                        </IconBtn>
                    </Group>

                    <Divider style={{ height: '20px', margin: '0 2px' }} />

                    {/* Text Alignment */}
                    <Group>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('textAlign', { textAlign: 'left' })}
                            onClick={() => editor.chain().focus().setTextAlign('left').run()}
                            data-tooltip="Alinhar à Esquerda"
                            type="button"
                        >
                            <AiOutlineAlignLeft size={13} />
                        </IconBtn>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('textAlign', { textAlign: 'center' })}
                            onClick={() => editor.chain().focus().setTextAlign('center').run()}
                            data-tooltip="Centralizar"
                            type="button"
                        >
                            <AiOutlineAlignCenter size={13} />
                        </IconBtn>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('textAlign', { textAlign: 'right' })}
                            onClick={() => editor.chain().focus().setTextAlign('right').run()}
                            data-tooltip="Alinhar à Direita"
                            type="button"
                        >
                            <AiOutlineAlignRight size={13} />
                        </IconBtn>
                    </Group>

                    <Divider style={{ height: '20px', margin: '0 2px' }} />

                    {/* List Control Group - for managing bulleted and numbered lists */}
                    <Group>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('bulletList')}
                            onClick={() => editor.chain().focus().toggleBulletList().run()}
                            data-tooltip="Lista com Marcadores"
                            type="button"
                        >
                            <AiOutlineUnorderedList size={13} />
                        </IconBtn>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('orderedList')}
                            onClick={() => editor.chain().focus().toggleOrderedList().run()}
                            data-tooltip="Lista Numerada"
                            type="button"
                        >
                            <AiOutlineOrderedList size={13} />
                        </IconBtn>
                    </Group>

                    <Divider style={{ height: '20px', margin: '0 2px' }} />

                    {/* Hyperlink Group - for adding and managing hyperlinks */}
                    <Group>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            active={isActive('link')}
                            onClick={handleLinkButtonClick}
                            data-tooltip="Inserir Link"
                            type="button"
                        >
                            <MdOutlineInsertLink size={13} />
                        </IconBtn>
                    </Group>

                    {/* Format Clearing Group - removes all text formatting */}
                    <Group>
                        <IconBtn
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()}
                            data-tooltip="Limpar Formatação"
                            type="button"
                        >
                            <RiFormatClear size={13} />
                        </IconBtn>
                    </Group>
                </Bar>
            </BubbleMenu>

            {/* Floating menu with requested options */}
            <FloatingMenu
                editor={editor}
                shouldShow={({ editor }) => {
                    const { state } = editor;
                    const { from, empty } = state.selection;

                    // só exibe se a seleção estiver vazia
                    if (!empty) return false;

                    // posição do cursor no começo do documento
                    if (from === 0) return false;

                    // caractere imediatamente antes do cursor
                    const prev = state.doc.textBetween(from - 1, from, '\uFFFC', '\uFFFC');

                    return prev === '/';
                }}
                tippyOptions={{
                    placement: 'right-start',
                    maxWidth: 'none',
                    zIndex: 200
                }}>
                <Bar style={{
                    marginBottom: 0,
                    padding: '4px 6px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                    flexWrap: 'nowrap',
                    maxWidth: '100vw',
                    gap: '2px',
                    marginTop: '-10px',
                    borderRadius: '8px',
                    animation: 'fadeIn 0.2s ease'
                }}>
                    <Group>
                        {dynamicFields && dynamicFields.length > 0 && (
                            <IconBtn
                                onClick={(e) => {
                                    e.preventDefault();
                                    // Use cursor position for floating menu
                                    const position = calculateFloatingDropdownPosition();
                                    setBubblePosition(position);
                                    setShowBubbleDynamicFieldsDropdown(!showBubbleDynamicFieldsDropdown);
                                    deleteSlashBeforeCursor(editor);
                                }}
                                style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                                data-tooltip="Campos Dinâmicos"
                                type="button"
                            >
                                <BsLightningCharge color='red' size={12} />
                            </IconBtn>
                        )}
                        <IconBtn
                            onClick={() => {
                                editor.chain().focus().toggleHeading({ level: 1 }).run();
                                deleteSlashBeforeCursor(editor);
                            }}
                            active={isActive('heading', { level: 1 })}
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            data-tooltip="Título 1"
                            type="button"
                        >
                            <span style={{ fontWeight: 'bold', fontSize: '12px' }}>H1</span>
                        </IconBtn>
                        <IconBtn
                            onClick={() => {
                                editor.chain().focus().toggleHeading({ level: 2 }).run()
                                deleteSlashBeforeCursor(editor);
                            }}
                            active={isActive('heading', { level: 2 })}
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            data-tooltip="Título 2"
                            type="button"
                        >
                            <span style={{ fontWeight: 'bold', fontSize: '12px' }}>H2</span>
                        </IconBtn>
                        <IconBtn
                            onClick={() => {
                                editor.chain().focus().toggleBulletList().run()
                                deleteSlashBeforeCursor(editor);
                            }}
                            active={isActive('bulletList')}
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            data-tooltip="Lista com Marcadores"
                            type="button"
                        >
                            <AiOutlineUnorderedList size={13} />
                        </IconBtn>

                        {!disableImage && (
                            <IconBtn
                                onClick={() => {
                                    insertLocalImage(editor);
                                    deleteSlashBeforeCursor(editor);
                                }}
                                style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                                data-tooltip="Inserir Imagem"
                                type="button"
                            >
                                <AiOutlinePicture size={13} />
                            </IconBtn>
                        )}
                        <IconBtn
                            onClick={() => {
                                editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()
                                deleteSlashBeforeCursor(editor);
                            }}
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            data-tooltip="Inserir Tabela"
                            type="button"
                        >
                            <AiOutlineTable size={13} />
                        </IconBtn>
                        <IconBtn
                            onClick={() => {
                                editor.chain().focus().setHorizontalRule().run()
                                deleteSlashBeforeCursor(editor);
                            }}
                            style={{ height: '28px', width: '28px', padding: 0, minWidth: '28px' }}
                            data-tooltip="Inserir Divisor"
                            type="button"
                        >
                            <AiOutlineLine size={13} />
                        </IconBtn>

                    </Group>
                </Bar>
            </FloatingMenu>

            {/* Dropdown portals - render outside the normal flow for proper positioning */}
            {/* Dynamic fields dropdown portal */}
            {showBubbleDynamicFieldsDropdown && dynamicFields && (
                <ClickAwayListener onClickAway={handleClickAway}>
                    <DropdownContent
                        className="dynamic-fields-dropdown portal-dropdown"
                        style={{
                            position: 'fixed', // Changed from absolute to fixed
                            top: `${bubblePosition.top}px`,
                            left: `${bubblePosition.left}px`,
                            zIndex: 2000,
                            minWidth: '250px',
                            maxWidth: '350px',
                            maxHeight: '400px',
                            overflowY: 'auto',
                            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                            borderRadius: '12px',
                            border: '1px solid rgba(0, 0, 0, 0.05)',
                            padding: '10px',
                            animation: 'fadeIn 0.2s cubic-bezier(0.2, 0, 0, 1)'
                        }}
                        onKeyDown={(e) => {
                            // Prevenção padrão para evitar comportamentos inesperados
                            if (e.key === 'Tab' || e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                                e.preventDefault();
                            }

                            const allFilteredItems = sortedFieldGroups
                                .flatMap(([_, fields]) =>
                                    fields.filter(field =>
                                        field.label.toLowerCase().includes(searchTerm.toLowerCase())
                                    )
                                );

                            const itemCount = allFilteredItems.length;

                            // Navegação com Tab e Shift+Tab
                            if (e.key === 'Tab') {
                                if (e.shiftKey) {
                                    // Shift+Tab: voltar para o item anterior
                                    setFocusedItemIndex(prev => (prev <= 0 ? itemCount - 1 : prev - 1));
                                } else {
                                    // Tab: avançar para o próximo item
                                    setFocusedItemIndex(prev => (prev >= itemCount - 1 ? 0 : prev + 1));
                                }
                            }

                            // Navegação com setas
                            else if (e.key === 'ArrowDown') {
                                setFocusedItemIndex(prev => (prev >= itemCount - 1 ? 0 : prev + 1));
                            }
                            else if (e.key === 'ArrowUp') {
                                setFocusedItemIndex(prev => (prev <= 0 ? itemCount - 1 : prev - 1));
                            }

                            // Seleção com Enter
                            else if (e.key === 'Enter' && focusedItemIndex >= 0 && focusedItemIndex < itemCount) {
                                const selectedField = allFilteredItems[focusedItemIndex];
                                if (onAddDynamicField && selectedField) {
                                    onAddDynamicField({
                                        title: selectedField.label,
                                        id_field: selectedField.value,
                                        name: selectedField.value,
                                        type: selectedField.type || "TEXT_SHORT_FIELD"
                                    });
                                    setShowBubbleDynamicFieldsDropdown(false);
                                }
                            }

                            // Fechar com Escape
                            else if (e.key === 'Escape') {
                                setShowBubbleDynamicFieldsDropdown(false);
                                if (editor) {
                                    editor.commands.focus();
                                }
                            }
                        }}
                        tabIndex={0} // Permite que o container receba foco
                    >
                        {/* Campo de busca */}
                        <div style={{
                            padding: '0 0 10px 0',
                            position: 'sticky',
                            top: 0,
                            backgroundColor: 'white',
                            zIndex: 10,
                            borderBottom: '1px solid #eee',
                            marginBottom: '8px'
                        }}>
                            <input
                                ref={searchInputRef}
                                type="text"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setFocusedItemIndex(-1); // Reset focus quando pesquisa muda
                                }}
                                placeholder="Buscar campo..."
                                style={{
                                    width: '100%',
                                    padding: '8px 12px',
                                    borderRadius: '6px',
                                    border: '1px solid #ddd',
                                    fontSize: '14px',
                                    outline: 'none'
                                }}
                                autoFocus
                            />
                        </div>

                        {sortedFieldGroups.map(([group, fields], groupIndex) => {
                            // Filtrar campos pelo termo de busca
                            const filteredFields = fields.filter(field =>
                                field.label.toLowerCase().includes(searchTerm.toLowerCase())
                            );

                            // Se não houver campos após filtrar, não mostrar o grupo
                            if (filteredFields.length === 0) return null;

                            return (
                                <div key={`floating-group-${groupIndex}`} style={{ marginBottom: '15px' }}>
                                    <OptionTitle>{group}</OptionTitle>
                                    {filteredFields.map((field, fieldIndex) => {
                                        // Encontrar o índice global deste item para o foco
                                        const globalIndex = sortedFieldGroups
                                            .slice(0, groupIndex)
                                            .reduce((acc, [_, groupFields]) =>
                                                acc + groupFields.filter(f =>
                                                    f.label.toLowerCase().includes(searchTerm.toLowerCase())
                                                ).length, 0) + filteredFields.slice(0, fieldIndex).length;

                                        const isFocused = focusedItemIndex === globalIndex;
                                        const Icon = field.icon !== undefined ? field.icon : FaPlug;

                                        function handleDynamicFieldClick(e: React.MouseEvent) {
                                            e.stopPropagation();
                                            e.preventDefault();

                                            if (onAddDynamicField) {
                                                onAddDynamicField({
                                                    title: field.label,
                                                    id_field: field.value,
                                                    name: field.value,
                                                    type: field.type || "TEXT_SHORT_FIELD"
                                                });
                                            }
                                            setShowBubbleDynamicFieldsDropdown(false);
                                        }

                                        return (
                                            <OptionItem
                                                key={fieldIndex}
                                                color={field.color !== undefined ? field.color : "#bbbbbb"}
                                                onClick={handleDynamicFieldClick}
                                                onMouseEnter={() => setFocusedItemIndex(globalIndex)}
                                                style={{
                                                    backgroundColor: isFocused ? '#f5f5f5' : 'transparent',
                                                    outline: isFocused ? '1px solid #f23b5c' : 'none',
                                                    cursor: 'pointer'
                                                }}
                                                tabIndex={-1} // Evita foco direto nos itens
                                            >
                                                <OptionItemIcon color={field.color !== undefined ? field.color : "#bbbbbb"}>
                                                    {field.iconDynamic !== undefined ?
                                                        <IconPickerItem
                                                            icon={field.iconDynamic as IconList}
                                                            color={field.color !== undefined ? field.color : "#bbbbbb"}
                                                        /> :
                                                        <Icon />
                                                    }
                                                </OptionItemIcon>
                                                <OptionItemDescription>
                                                    <h3>{field.label}</h3>
                                                    <span>{field.description}</span>
                                                </OptionItemDescription>
                                            </OptionItem>
                                        )
                                    })}
                                </div>
                            );
                        })}

                        {/* Mensagem quando nenhum resultado é encontrado */}
                        {searchTerm && !sortedFieldGroups.some(([_, fields]) =>
                            fields.some(field => field.label.toLowerCase().includes(searchTerm.toLowerCase()))
                        ) && (
                                <div style={{
                                    padding: '20px',
                                    textAlign: 'center',
                                    color: '#666'
                                }}>
                                    Nenhum campo encontrado com "{searchTerm}"
                                </div>
                            )}
                    </DropdownContent>
                </ClickAwayListener>
            )}

            {/* Font size dropdown portal */}
            {showBubbleFontSizeDropdown && (
                <DropdownContent
                    className="font-style-dropdown portal-dropdown"
                    style={{
                        position: 'fixed', // Changed from absolute to fixed
                        top: `${bubblePosition.top}px`,
                        left: `${bubblePosition.left}px`,
                        zIndex: 1000,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        animation: 'fadeIn 0.2s cubic-bezier(0.2, 0, 0, 1)'
                    }}
                >
                    <DropdownItem
                        onClick={() => {
                            editor.chain().focus().setParagraph().run();
                            setShowBubbleFontSizeDropdown(false);
                        }}
                        active={isActiveHeading('paragraph')}
                        type="button"
                    >
                        <span>Normal</span>
                    </DropdownItem>
                    <DropdownItem
                        onClick={() => {
                            editor.chain().focus().toggleHeading({ level: 1 }).run();
                            setShowBubbleFontSizeDropdown(false);
                        }}
                        active={isActive('heading', { level: 1 })}
                        type="button"
                    >
                        <span style={{ fontWeight: 'bold', fontSize: '1.3em' }}>
                            Título 1
                        </span>
                    </DropdownItem>
                    <DropdownItem
                        onClick={() => {
                            editor.chain().focus().toggleHeading({ level: 2 }).run();
                            setShowBubbleFontSizeDropdown(false);
                        }}
                        active={isActive('heading', { level: 2 })}
                        type="button"
                    >
                        <span style={{ fontWeight: 'bold', fontSize: '1.1em' }}>
                            Título 2
                        </span>
                    </DropdownItem>
                    <DropdownItem
                        onClick={() => {
                            editor.chain().focus().toggleHeading({ level: 3 }).run();
                            setShowBubbleFontSizeDropdown(false);
                        }}
                        active={isActive('heading', { level: 3 })}
                        type="button"
                    >
                        <span style={{ fontWeight: 'bold', fontSize: '0.9em' }}>
                            Título 3
                        </span>
                    </DropdownItem>
                    <DropdownItem
                        onClick={() => {
                            editor.chain().focus().toggleHeading({ level: 4 }).run();
                            setShowBubbleFontSizeDropdown(false);
                        }}
                        active={isActive('heading', { level: 4 })}
                        type="button"
                    >
                        <span style={{ fontWeight: 'bold', fontSize: '0.8em' }}>
                            Título 4
                        </span>
                    </DropdownItem>
                    <DropdownItem
                        onClick={() => {
                            editor.chain().focus().setParagraph().setFontSize('10px').run();
                            setShowBubbleFontSizeDropdown(false);
                        }}
                        active={isActiveHeading('small')}
                        type="button"
                    >
                        <span style={{ fontSize: '0.7em' }}>
                            Texto Pequeno
                        </span>
                    </DropdownItem>
                </DropdownContent>
            )}

            {/* Font color dropdown portal */}
            {showBubbleFontColorDropdown && (
                <DropdownContent
                    className="color-dropdown portal-dropdown"
                    style={{
                        position: 'fixed', // Changed from absolute to fixed
                        top: `${bubblePosition.top}px`,
                        left: `${bubblePosition.left}px`,
                        zIndex: 1000,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        animation: 'fadeIn 0.2s cubic-bezier(0.2, 0, 0, 1)'
                    }}
                >
                    <ColorPicker>
                        {fontColors.map(color => (
                            <div
                                key={color}
                                style={{ backgroundColor: color }}
                                onClick={() => {
                                    setFontColor(color);
                                    setShowBubbleFontColorDropdown(false);
                                }}
                                className={editor.getAttributes('textStyle').color === color ? 'active' : ''}
                            />
                        ))}
                    </ColorPicker>
                </DropdownContent>
            )}

            {/* Highlight dropdown portal */}
            {showBubbleHighlightDropdown && (
                <DropdownContent

                    className="highlight-dropdown portal-dropdown"
                    style={{
                        position: 'fixed', // Changed from absolute to fixed
                        top: `${bubblePosition.top}px`,
                        left: `${bubblePosition.left}px`,
                        zIndex: 1000,
                        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 0, 0, 0.05)',
                        animation: 'fadeIn 0.2s cubic-bezier(0.2, 0, 0, 1)'
                    }}
                >
                    <ColorPicker>
                        {highlightColors.map(color => (
                            <div
                                key={color}
                                style={{ backgroundColor: color }}
                                onClick={() => {
                                    setHighlight(color);
                                    setShowBubbleHighlightDropdown(false);
                                }}
                                className={isActive('highlight', { color }) ? 'active' : ''}
                            />
                        ))}
                    </ColorPicker>
                    <RemoveHighlightBtn
                        onClick={() => {
                            editor.chain().focus().unsetHighlight().run();
                            setShowBubbleHighlightDropdown(false);
                        }}
                        type="button"
                    >
                        Remover Destaque
                    </RemoveHighlightBtn>
                </DropdownContent>
            )}

            {/* Link input dropdown - keep this separate */}
            {
                showLinkInput && (
                    <DropdownContent
                        className="link-dropdown"
                        style={{
                            position: 'fixed', // Changed from absolute to fixed
                            padding: '16px',
                            minWidth: '280px',
                            top: `${dropdownPosition.top}px`,
                            left: `${dropdownPosition.left}px`,
                            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                            zIndex: 1000,
                            borderRadius: '12px',
                            animation: 'fadeIn 0.2s cubic-bezier(0.2, 0, 0, 1)'
                        }}
                    >
                        <div style={{
                            marginBottom: '12px',
                            fontSize: '14px',
                            fontWeight: 500,
                            color: '#333',
                            borderBottom: '1px solid #eee',
                            paddingBottom: '8px'
                        }}>
                            Inserir Link
                        </div>
                        <input
                            type="text"
                            placeholder="https://exemplo.com"
                            value={linkUrl}
                            onChange={(e) => setLinkUrl(e.target.value)}
                            style={{
                                width: '100%',
                                padding: '10px 12px',
                                borderRadius: '6px',
                                border: '1px solid #DDDDDD',
                                fontSize: '14px',
                                marginBottom: '16px',
                                transition: 'all 0.2s ease',
                                outline: 'none'
                            }}
                            onFocus={(e) => {
                                e.target.style.borderColor = '#4A86E8';
                                e.target.style.boxShadow = '0 0 0 2px rgba(74, 134, 232, 0.2)';
                            }}
                            onBlur={(e) => {
                                e.target.style.borderColor = '#DDDDDD';
                                e.target.style.boxShadow = 'none';
                            }}
                            autoFocus
                        />
                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px'
                        }}>
                            <Btn
                                onClick={() => setShowLinkInput(false)}
                                style={{
                                    padding: '8px 16px',
                                    background: '#f5f5f5',
                                    color: '#333',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500
                                }}
                                type="button"
                            >
                                Cancelar
                            </Btn>
                            <Btn
                                onClick={setLink}
                                style={{
                                    padding: '8px 16px',
                                    background: '#4A86E8',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                                }}
                                type="button"
                            >
                                Aplicar
                            </Btn>
                        </div>
                    </DropdownContent>
                )
            }
        </div >
    );
};

export default TipTapEditor;